import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useReactMediaRecorder } from "react-media-recorder";
import { useStopwatch } from "react-timer-hook";
import AudioComponent from "./audioComponent";
import {
    audioRecordContainerStyles,
    recordingDotStyles,
    recordingStopButtonStyles,
    clearButtonStyles,
    submitButtonStyles,
    primaryButtonStyles
} from "../../styles/aiStyles";
import { AI_COLORS } from "../../styles/aiTheme";

interface AudioRecordComponentProps {
    audioFile: File | null,
    setAudioFile: React.Dispatch<React.SetStateAction<File | null>>,
    processFunction?: () => Promise<void>;
    label: string;
    limitInMS: number;
};

const AudioRecordComponent: React.FC<AudioRecordComponentProps> = ({ audioFile, setAudioFile, processFunction, label, limitInMS }) => {
    const [isAudioRecording, setIsAudioRecording] = useState<boolean>(false);
    const [blobUrl, setBlobUrl] = useState<string>('');
    const { start, reset, minutes, seconds, milliseconds } = useStopwatch({ autoStart: false });

    const startAudioRecording = () => {
        setIsAudioRecording(true);
        start();
    };

    const stopAudioRecording = (blobURL: string, blob: Blob) => {
        reset(undefined, false);
        setBlobUrl(blobURL);
        const file = new File([blob], 'sound.mp4', { type: "audio/mp4" });
        setAudioFile(file);
        setIsAudioRecording(false);
    };

    const { startRecording, stopRecording, status } = useReactMediaRecorder({
        audio: true,
        blobPropertyBag: { type: "audio/mp4" },
        onStart: startAudioRecording,
        onStop: stopAudioRecording,
    });

    const clearRecording = () => {
        setBlobUrl('');
        setAudioFile(null);
    };

    useEffect(() => {
        const ms = minutes * 60000 + seconds * 1000 + milliseconds;
        if (ms >= limitInMS) {
            stopRecording();
        }
    }, [seconds]);

    useEffect(() => {
        return () => {
            if (status === "recording") {
                stopRecording();
            }
            clearRecording();
        };
    }, []);

    return (
        <Box sx={audioRecordContainerStyles}>
            <Typography sx={{
                color: AI_COLORS.text.secondary,
                mb: 2.5,
                fontWeight: 600,
                fontSize: '0.95rem',
                letterSpacing: '0.5px'
            }}>
                {label}
            </Typography>
            {isAudioRecording ? (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={recordingDotStyles} />
                        <Typography sx={{
                            fontWeight: 'bold',
                            fontSize: '1.1rem',
                            color: AI_COLORS.text.primary,
                            fontFamily: 'monospace'
                        }}>
                            {minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}:{milliseconds.toString().padStart(2, '0')}
                        </Typography>
                    </Box>
                    <Button
                        onClick={stopRecording}
                        sx={recordingStopButtonStyles}
                    >
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>⏹ Stop Recording</Typography>
                    </Button>
                </Box>
            ) : (
                <>
                    {audioFile && blobUrl ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <AudioComponent src={blobUrl} />
                            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                <Button
                                    onClick={clearRecording}
                                    sx={clearButtonStyles}
                                >
                                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>🗑 Clear</Typography>
                                </Button>
                                {processFunction ? (
                                    <Button
                                        onClick={processFunction}
                                        sx={submitButtonStyles}
                                    >
                                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>✨ Submit</Typography>
                                    </Button>
                                ) : null}
                            </Box>
                        </Box>
                    ) : (
                        <Button
                            onClick={startRecording}
                            sx={primaryButtonStyles}
                        >
                            <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>🎤 Start Recording</Typography>
                        </Button>
                    )}
                </>
            )}
        </Box>
    );
};

export default AudioRecordComponent;
